import React, { useCallback, useContext, useMemo, useState } from "react"
import { ICurrentUser, IGlobalState, useGlobalState } from "./GlobalStateProvider"
import { useAsyncEffect } from "../hooks/useAsyncEffect"

const DEFAULT_BRANCH_ID = "5d632b17-1409-446f-81c0-e8c6a4703052"

const anonymousUser = {
  id: null as any,
  isAdmin: false,
  isSuperAdmin: false,
  canExportPurchaseOrdersToSage: false,
  canEditGlobalUserPermissions: false,
  canManageProducts: false,
  canManageConsignmentProducts: false,
  isAccounts: false,
  isHrAdmin: false,
  hrManagedUsers: [],
  managedByHrUsers: [],
  hrAreaOnly: false,
  name: "",
  email: "",
  branch: {
    id: DEFAULT_BRANCH_ID,
    name: "HUB",
    currency_name: "GBP",
    consignmentWarehouse: undefined,
    euro_pound_exchange_rate: 1,
  } as any,
  userAccountType: "external",
  customer: undefined,
  sub_customers: [],
  accessibleBranches: [],
  hrAccessibleBranches: [],
  canRunSageImports: false,
  canViewCRM: false,
  canViewBranchReports: false,
  isBlocked: false,
  canAccessSystemSettings: false,
  canEditBranchSettings: false,
  canUseNonPriceBandPricesOnQuotesOrders: false,
  canSetPricesBelowMinimumMargins: false,
  canEditCustomersPricing: false,
  canViewGlobalMasterPriceImports: false,
  microsoft_oauth: {},
  beta_features: {},
  accessible_reports: {},
  defaultWarehouse: undefined,
  canManageBetaFeatureAccess: false,
  canViewPreviewFeatures: false,
  canUpdatePurchaseOrdersInSage: false,
  canManageSupportTicketSettings: false,
  canCreateSageProducts: false,
  canEditExistingProducts: false,
  dashboard_type: "",
  salesRepId: null,
  canManageStockTakes: false,
  canManageSalesRepTargets: false,
  canManageBranchTargets: false,
  hasExistingTradeAccount: false,
  first_name: "",
  last_name: "",
  companyName: "",
  canDispatchOverCreditLimitOrders: false,
  canGrantReportPermissions: false,
  canManageSalesRepAssignments: false,
  canDeletePriceBands: false,
  canCreateStockTransfers: false,
  canManageUniversalPricing: false,
  canUpdateManagingUsersOnSharePriceBands: false,
  canCopyPriceBandsInterBranch: false,
  canSendBulkEmails: false,
  customerIsPrimaryAccountHolder: false,
  customerCanViewFinances: false,
  canViewFortusLiveAccounts: false,
  canManageCustomerTags: false,
  canManageHolidayRequests: false,
  reports_viewable_branches: [],
  accessibleBulkEmailBranches: [],
  canViewOtherUsersEvents: false,
  canArchiveQuotes: false,
  canArchiveOrders: false,
  canExportCustomers: false,
  canManageCRMPermissions: false,
  canManageHRPermissions: false,
  enablePhoneMFA: false,
  canViewCustomerStatements: false,
  customer_view_only: false,
  canMigrateSalesRepInvoiceItems: false,
  canViewCustomerLists: false,
  canEditOrderRequests: false,
  canExportNewProducts: false,
  canAssignCustomerTags: false,
  canDeleteUnexportedProducts: false,
  canManageProductMasterTags: false,
  canAssignProductMasterTags: false,
  canViewInternalTickets: false,
  can_approve_manual_invoice_items: false,
  can_create_manual_invoice_items: false,
  canBypassSpendLimitOnNewBusiness: false,
  preferences: {} as any,
  country: "uk",
  customerAreaQuotesOnly: false,
  canManageBuyingGroups: false,
  canApproveByPassMinMargins: false,
  canManageNamedPriceBands: false,
  can_set_site_banner_message: false,
  canEditLinkTooltips: false,
  microsoft_user_details: null,
  defaultSupportTicketGroup: null,
  defaultCustomerTagFilter: null,
  canManageRoles: false,
  can_manage_sage_auth: false,
  can_manage_customer_slide_show: false,
  canDeleteUniversalPriceBands: false,
  canManageSharedPriceBands: false,
}

export interface IWebsiteState extends IGlobalState {
  isAnonymous: boolean
  translateBranchName: (name: string) => string
}

export const WebsiteContext = React.createContext<IWebsiteState | null>(null)

export function useWebsiteUser(): IWebsiteState {
  const value = useContext(WebsiteContext)
  if (!value) {
    throw new Error("must use WebsiteProvider")
  }
  return value
}

let _displayNameMapPromise: Promise<Map<string, string>> | null = null

function loadBranchDisplayNameMap() {
  if (!_displayNameMapPromise) {
    _displayNameMapPromise = window.skeem
      .fetch2<{ name: string; settings?: { branchName?: string } }[]>({
        model: {
          name: "branches",
          attributes: ["name", "settings"],
        },
      })
      .then(
        records =>
          new Map(records.map(branch => [branch.name, branch.settings?.branchName || branch.name])),
      )
      .catch(error => {
        console.error(error)
        _displayNameMapPromise = null
        return new Map<string, string>()
      })
  }
  return _displayNameMapPromise
}

interface IProps {
  children: React.ReactNode
}

export function WebsiteProvider({ children }: IProps) {
  const globalState = useGlobalState()
  const [displayNameMap, setDisplayNameMap] = useState<Map<string, string>>(() => new Map())

  useAsyncEffect(async () => {
    const map = await loadBranchDisplayNameMap()
    setDisplayNameMap(map)
  }, [])

  const translateBranchName = useCallback(
    (name: string) => displayNameMap.get(name) || name,
    [displayNameMap],
  )

  const isAnonymous = !globalState.signedIn
  const baseUser = isAnonymous
    ? (anonymousUser as unknown as ICurrentUser)
    : (globalState.currentUser as ICurrentUser)
  const currentUser = useMemo<ICurrentUser>(
    () => ({
      ...baseUser,
      branch: {
        ...baseUser.branch,
        name: translateBranchName(baseUser.branch?.name || ""),
      },
    }),
    [baseUser, translateBranchName],
  )

  const value: IWebsiteState = {
    ...globalState,
    currentUser,
    isAnonymous,
    translateBranchName,
  }

  return <WebsiteContext.Provider value={value}>{children}</WebsiteContext.Provider>
}
